/** @format */

import {Switch as SwitchUI} from '@headlessui/react'
import clsx from 'clsx'
import React, {useEffect, useMemo, useRef, useState} from 'react'
import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'

import {
  ContextModal,
  useModalContext,
} from '@src/components/tailwind/ModalContext'
import {Progress} from '@src/components/tailwind/Progress'
import {RadioGroup} from '@src/components/tailwind/RadioButton'
import {RequireFeature} from '@src/components/tailwind/RequireFeature'
import {UpgradeMessage} from '@src/components/tailwind/UpgradeMessage'

import {useFormApi, useLoadingContext} from '@src/contexts'
import {useFetch} from '@src/hooks/useFetch'
import * as toast from '@src/toast'

import {toExportPayload, validateExportOptions} from './types/export'
import type {ExportModalState} from './types/export'

type ExportModalProps = {
  // the identifier of this modal
  modal: string
  // Context for available export modes
  selectedIds?: string[] // ids of selected submissions
  searchQuery?: string
  searchResultsCount?: number
  currentFolder?: 'inbox' | 'spam'
}

export function ExportModal(props: ExportModalProps) {
  const {modal, ...contextProps} = props
  const {current: currentForm} = useFormApi()
  return (
    <ContextModal title="Export submissions" modal={modal}>
      <RequireFeature
        fallback={
          <UpgradeMessage alignButton="end" feature="export">
            Export your submissions to CSV or JSON for further processing. Open
            with Microsoft Excel, or use your own scripts
          </UpgradeMessage>
        }
        feature="export"
        on={currentForm}
      >
        <ExportModalBody modal={modal} {...contextProps} />
      </RequireFeature>
    </ContextModal>
  )
}

type Format = 'csv' | 'json'

type ExportModalBodyProps = {
  modal: string
  selectedIds?: string[]
  searchQuery?: string
  searchResultsCount?: number
  currentFolder?: 'inbox' | 'spam'
}

function ExportModalBody(props: ExportModalBodyProps) {
  const {
    modal,
    selectedIds = [],
    searchQuery,
    searchResultsCount = 0,
    currentFolder = 'inbox',
  } = props

  const {current: currentForm} = useFormApi()
  const {ready} = useLoadingContext()
  const [format, setFormat] = useState<Format | undefined>()
  const {isOpen} = useModalContext()

  const fetch = useFetch()
  const {data: counts} = useSWR(
    isOpen(modal)
      ? `/api-int/forms/${currentForm.hashid}/submissions/counts`
      : null,
    async endpoint => {
      type Response = {
        inbox_count: number
        spam_count: number
      }

      return (await fetch(endpoint, {method: 'GET'})) as Response
    },
    {
      revalidateOnFocus: false,
    },
  )
  const requestExport = useSWRMutation<
    {export_id: string},
    Error,
    string,
    ExportModalState
  >(
    `/api-int/forms/${currentForm.hashid}/export`,
    async (url, {arg}) => {
      // Validate before sending
      const validationError = validateExportOptions(arg.exportOptions)
      if (validationError) {
        throw new Error(validationError)
      }

      const response = await fetch(url, {
        method: 'POST',
        payload: toExportPayload(arg),
      })

      if (!response || typeof response !== 'object') {
        throw new Error('Invalid response from server')
      }

      return response as {export_id: string}
    },
    {
      onError: (error: Error) => {
        toast.error(error.message || 'Failed to export submissions')
      },
    },
  )

  async function handleExportRequested(state: ExportModalState): Promise<void> {
    await requestExport.trigger(state)
    setFormat(state.format)
    ready()
  }

  function handleFormatReset(): void {
    requestExport.reset()
    setFormat(undefined)
  }

  const exportId = requestExport.data?.export_id
  if (!format || !exportId) {
    return (
      <SelectFormat
        onSelectFormat={handleExportRequested}
        selectedIds={selectedIds}
        searchQuery={searchQuery}
        searchResultsCount={searchResultsCount}
        currentFolder={currentFolder}
        inboxCount={counts?.inbox_count ?? 0}
        spamCount={counts?.spam_count ?? 0}
      />
    )
  }

  return <ExportStatus exportId={exportId} onFormatReset={handleFormatReset} />
}

type SelectFormatProps = {
  onSelectFormat: (state: ExportModalState) => void
  selectedIds: string[]
  searchQuery?: string
  searchResultsCount: number
  currentFolder: 'inbox' | 'spam'
  inboxCount: number
  spamCount: number
}

type ExportOptionConfig = {
  key: string
  label: string
  count: number
  enabled: boolean
  setEnabled: (enabled: boolean) => void
  type: 'selected' | 'search' | 'folder'
  folderType?: 'inbox' | 'spam'
}

function SelectFormat(props: SelectFormatProps) {
  const {
    onSelectFormat,
    selectedIds,
    searchQuery,
    searchResultsCount,
    currentFolder,
    inboxCount,
    spamCount,
  } = props

  const hasSelection = selectedIds.length > 0
  const hasSearch = !!searchQuery

  const [format, setFormat] = useState<Format>('csv')
  const [inboxFolderChecked, setInboxFolderChecked] = useState(
    currentFolder === 'inbox',
  )
  const [spamFolderChecked, setSpamFolderChecked] = useState(
    currentFolder === 'spam',
  )
  const [isSelectedSubmissionsChecked, setIsSelectedSubmissionsChecked] =
    useState(hasSelection)
  const [searchEnabled, setSearchEnabled] = useState(hasSearch)
  const [validationError, setValidationError] = useState<string | null>(null)

  // Unified export options list
  const exportOptions = useMemo<ExportOptionConfig[]>(() => {
    const options: ExportOptionConfig[] = []

    // Add selected submissions option if there are selected items
    if (hasSelection) {
      options.push({
        key: 'selected',
        label: 'Selected submissions',
        count: selectedIds.length,
        enabled: isSelectedSubmissionsChecked,
        setEnabled: setIsSelectedSubmissionsChecked,
        type: 'selected',
      })
    }

    // Add search results option if there's a search query
    if (hasSearch) {
      options.push({
        key: 'search',
        label: 'Search results',
        count: searchResultsCount,
        enabled: searchEnabled,
        setEnabled: setSearchEnabled,
        type: 'search',
      })
    }

    // Add folder options
    options.push(
      {
        key: 'inbox',
        label: 'Inbox',
        count: inboxCount,
        enabled: inboxFolderChecked,
        setEnabled: setInboxFolderChecked,
        type: 'folder',
        folderType: 'inbox',
      },
      {
        key: 'spam',
        label: 'Spam',
        count: spamCount,
        enabled: spamFolderChecked,
        setEnabled: setSpamFolderChecked,
        type: 'folder',
        folderType: 'spam',
      },
    )

    return options
  }, [
    hasSelection,
    hasSearch,
    selectedIds.length,
    searchResultsCount,
    inboxCount,
    spamCount,
    isSelectedSubmissionsChecked,
    searchEnabled,
    inboxFolderChecked,
    spamFolderChecked,
  ])

  function handleExport() {
    setValidationError(null)

    const exportPayloadOptions: any[] = []

    // Build export options from the unified list
    exportOptions.forEach(option => {
      if (!option.enabled) return

      switch (option.type) {
        case 'selected':
          exportPayloadOptions.push({
            mode: 'selected',
            submissionIds: selectedIds,
          })
          break
        case 'search':
          exportPayloadOptions.push({mode: 'search', query: searchQuery})
          break
        case 'folder':
          if (option.folderType) {
            exportPayloadOptions.push({
              mode: 'folder',
              folder: option.folderType,
            })
          }
          break
      }
    })

    if (exportPayloadOptions.length === 0) {
      setValidationError('Please select at least one option to export')
      return
    }

    onSelectFormat({format, exportOptions: exportPayloadOptions})
  }

  useEffect(() => {
    if (
      hasSelection ||
      hasSearch ||
      isSelectedSubmissionsChecked ||
      searchEnabled
    ) {
      setInboxFolderChecked(false)
      setSpamFolderChecked(false)
    }
  }, [hasSelection, hasSearch, isSelectedSubmissionsChecked, searchEnabled])

  // Calculate total items from enabled options
  // Logic: If a folder is selected, count only that folder (since selected/search
  // items from that folder are already included in the folder count).
  // If folder is not selected, count selected/search items separately.
  const totalItems = useMemo(() => {
    let total = 0
    const isViewingInbox = currentFolder === 'inbox'
    const isViewingSpam = currentFolder === 'spam'

    // Add inbox folder count if enabled
    if (inboxFolderChecked) {
      total += inboxCount
    }

    // Add spam folder count if enabled
    if (spamFolderChecked) {
      total += spamCount
    }

    // Add search results if enabled
    if (searchEnabled) {
      // Only add if the current folder being viewed is NOT already selected
      const currentFolderSelected =
        (isViewingInbox && inboxFolderChecked) ||
        (isViewingSpam && spamFolderChecked)

      if (!currentFolderSelected) {
        total += searchResultsCount
      }
    }

    // Add selected submissions if enabled
    if (isSelectedSubmissionsChecked) {
      // Only add if the current folder being viewed is NOT already selected
      const currentFolderSelected =
        (isViewingInbox && inboxFolderChecked) ||
        (isViewingSpam && spamFolderChecked)

      if (!currentFolderSelected) {
        total += selectedIds.length
      }
    }

    return total
  }, [
    inboxFolderChecked,
    spamFolderChecked,
    searchEnabled,
    isSelectedSubmissionsChecked,
    currentFolder,
    inboxCount,
    spamCount,
    searchResultsCount,
    selectedIds.length,
  ])

  return (
    <div className="grid gap-y-6">
      {/* Export options */}
      <fieldset className="grid gap-y-3">
        <legend className="mb-2 text-sm font-medium text-gray-700">
          What to export
        </legend>

        {/* Render all export options from unified list */}
        {exportOptions.map(option => (
          <label
            key={option.key}
            className="flex cursor-pointer items-center justify-between"
          >
            <span className="flex items-center gap-x-2">
              <span>{option.label}</span>
              <span className="text-sm text-gray-500">({option.count})</span>
            </span>
            <SwitchUI
              checked={option.enabled}
              onChange={option.setEnabled}
              className={clsx(
                'focus:ring-light-blue-500 relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2',
                'cursor-pointer',
                option.enabled ? 'bg-secondary' : 'bg-gray-200',
              )}
            >
              <span className="sr-only">Toggle {option.label}</span>
              <span
                aria-hidden="true"
                className={clsx(
                  option.enabled ? 'translate-x-5' : 'translate-x-0',
                  'inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                )}
              />
            </SwitchUI>
          </label>
        ))}
      </fieldset>

      {/* Format selection */}
      <div>
        <RadioGroup
          label={
            <span className="text-sm font-medium text-gray-700">Format</span>
          }
          value={format}
          onValueChange={value => setFormat(value as Format)}
          orientation="horizontal"
          options={[
            {value: 'csv', label: 'CSV'},
            {value: 'json', label: 'JSON'},
          ]}
        />
      </div>

      {/* Validation error */}
      {validationError && (
        <div className="rounded bg-red-50 p-3 text-sm text-red-700">
          {validationError}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex justify-end gap-x-3">
        <button
          className="buttonPrimary text-sm"
          onClick={handleExport}
          disabled={totalItems === 0}
        >
          Export{' '}
          {totalItems > 0 && `${totalItems} item${totalItems !== 1 ? 's' : ''}`}
        </button>
      </div>
    </div>
  )
}

type ExportStatusProps = {
  exportId: string
  // where to poll; the support page polls its own copy of the status endpoint
  statusEndpoint?: string
  onFormatReset: () => void
}

type ExportStatusData =
  | {
      status: 'PENDING'
      processed: number
      total: number
    }
  | {
      status: 'COMPLETED'
      url: string
    }

export function ExportStatus(props: ExportStatusProps) {
  const {
    exportId,
    statusEndpoint = `/api-int/forms/exports/${exportId}`,
    onFormatReset,
  } = props
  const refresh = useRef(true)

  const fetch = useFetch()
  const {data} = useSWR<ExportStatusData>(
    statusEndpoint,
    endpoint => fetch(endpoint, {method: 'GET'}),
    {
      fallbackData: {
        status: 'PENDING',
        processed: 0,
        total: 0,
      },
      onSuccess(data) {
        if (data.status === 'COMPLETED') {
          refresh.current = false
        }
      },
      refreshInterval: refresh.current ? 2000 : 0,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  )

  return data.status === 'PENDING' ? (
    <Pending {...data} />
  ) : (
    <Completed {...data} onFormatReset={onFormatReset} />
  )
}

type PendingProps = Extract<ExportStatusData, {status: 'PENDING'}>

function Pending(props: PendingProps) {
  const {processed, total} = props
  const progress = total > 0 ? Math.round((100 * processed) / total) : 0
  return (
    <div className="grid gap-y-2 pb-2">
      <p className="text-gray-600">Preparing your export...</p>
      <Progress value={progress} />
      <p className="text-right font-mono text-sm text-gray-400">
        {processed} of {total} submissions
      </p>
    </div>
  )
}

type CompletedProps = Extract<ExportStatusData, {status: 'COMPLETED'}> & {
  onFormatReset: () => void
}

function Completed(props: CompletedProps) {
  const {onFormatReset, url} = props
  const downloadLink = useRef<HTMLAnchorElement>(null)

  useEffect(() => {
    downloadLink.current?.click()
  }, [])

  return (
    <div className="grid gap-y-4 pb-2">
      <h3 className="font-semibold">Export Complete</h3>
      <p className="text-gray-600">
        <span>
          Your export is ready. If your file hasn't started downloading, you can
          try{' '}
        </span>
        <a className="primaryLink" href={url} ref={downloadLink}>
          downloading
        </a>
        <span> it again.</span>
      </p>
      <div className="flex justify-end">
        <button onClick={onFormatReset} className="buttonSecondary text-sm">
          Choose a different format
        </button>
      </div>
    </div>
  )
}
